'use client';

import { useState } from 'react';
import SaveCalculationModal from '@/components/common/SaveCalculationModal';
import { useAuth } from '@/contexts/AuthContext';
import Link from 'next/link';

const INITIAL_STATE = {
  currentAge: 30,
  retirementAge: 65,
  currentSavings: 50000,
  monthlyContribution: 500,
  returnRate: 7,
};

const PRESETS = {
  conservative: {
    currentAge: 30,
    retirementAge: 67,
    currentSavings: 30000,
    monthlyContribution: 400,
    returnRate: 5,
  },
  moderate: {
    currentAge: 30,
    retirementAge: 65,
    currentSavings: 50000,
    monthlyContribution: 500,
    returnRate: 7,
  },
  aggressive: {
    currentAge: 30,
    retirementAge: 60,
    currentSavings: 80000,
    monthlyContribution: 800,
    returnRate: 9,
  },
};

export default function CalculatorRetirement() {
  const [currentAge, setCurrentAge] = useState(INITIAL_STATE.currentAge);
  const [retirementAge, setRetirementAge] = useState(INITIAL_STATE.retirementAge);
  const [currentSavings, setCurrentSavings] = useState(INITIAL_STATE.currentSavings);
  const [monthlyContribution, setMonthlyContribution] = useState(INITIAL_STATE.monthlyContribution);
  const [returnRate, setReturnRate] = useState(INITIAL_STATE.returnRate);
  const [isCalculating, setIsCalculating] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const { user } = useAuth();

  const handleChange = (setter: (v: number) => void, value: number) => {
    setter(value);
    setIsCalculating(true);
    setTimeout(() => setIsCalculating(false), 800);
  };

  const yearsToRetirement = retirementAge - currentAge;
  const monthlyRate = returnRate / 100 / 12;
  const months = yearsToRetirement * 12;
  
  const futureValue = currentSavings * Math.pow(1 + monthlyRate, months) +
    monthlyContribution * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate);
  
  const totalContributions = currentSavings + (monthlyContribution * months);
  const totalGrowth = futureValue - totalContributions;

  const inputs = {
    currentAge,
    retirementAge,
    currentSavings,
    monthlyContribution,
    returnRate,
  };

  const results = {
    futureValue,
    totalContributions,
    totalGrowth,
    yearsToRetirement,
  };

  const handleReset = () => {
    setCurrentAge(INITIAL_STATE.currentAge);
    setRetirementAge(INITIAL_STATE.retirementAge);
    setCurrentSavings(INITIAL_STATE.currentSavings);
    setMonthlyContribution(INITIAL_STATE.monthlyContribution);
    setReturnRate(INITIAL_STATE.returnRate);
  };

  const handlePreset = (preset: keyof typeof PRESETS) => {
    const values = PRESETS[preset];
    setCurrentAge(values.currentAge);
    setRetirementAge(values.retirementAge);
    setCurrentSavings(values.currentSavings);
    setMonthlyContribution(values.monthlyContribution);
    setReturnRate(values.returnRate);
  };

  return (
    <div className="grid lg:grid-cols-5 gap-8">
      <div className="lg:col-span-3 space-y-8">
        <div className="bg-card rounded-xl p-8 border border-border">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-serif">Your Inputs</h2>
            {user && (
              <button
                onClick={() => setShowSaveModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                </svg>
                Save Result
              </button>
            )}
            {!user && (
              <Link
                href="/login"
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                </svg>
                Sign in to Save
              </Link>
            )}
          </div>

        <div className="space-y-6 mb-8">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Current Age</label>
              <input
                type="number"
                value={currentAge}
                onChange={(e) => handleChange(setCurrentAge, Number(e.target.value))}
                className="w-full px-3 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Retirement Age</label>
              <input
                type="number"
                value={retirementAge}
                onChange={(e) => handleChange(setRetirementAge, Number(e.target.value))}
                className="w-full px-3 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Current Savings</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">$</span>
              <input
                type="number"
                value={currentSavings}
                onChange={(e) => handleChange(setCurrentSavings, Number(e.target.value))}
                className="w-full pl-7 pr-3 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Monthly Contribution</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">$</span>
              <input
                type="number"
                value={monthlyContribution}
                onChange={(e) => handleChange(setMonthlyContribution, Number(e.target.value))}
                className="w-full pl-7 pr-3 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Expected Return Rate</label>
            <div className="relative">
              <input
                type="number"
                value={returnRate}
                onChange={(e) => handleChange(setReturnRate, Number(e.target.value))}
                step={0.1}
                className="w-full pl-3 pr-10 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">%</span>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-gradient-to-br from-purple-500 via-violet-500 to-indigo-600 rounded-xl p-6 text-white text-center">
            <div className="text-sm font-semibold uppercase tracking-wider opacity-90 mb-2">Retirement Savings</div>
            <div className="text-5xl font-bold">${futureValue.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
            <div className="text-sm opacity-90 mt-2">At age {retirementAge}</div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <div className="text-xs text-slate-600 mb-1">Total Contributions</div>
              <div className="text-xl font-bold text-slate-900">${totalContributions.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <div className="text-xs text-slate-600 mb-1">Investment Growth</div>
              <div className="text-xl font-bold text-slate-900">${totalGrowth.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
            </div>
          </div>
        </div>
      </div>
      </div>

      <SaveCalculationModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        calculatorType="retirement"
        inputs={inputs}
        results={results}
      />
    </div>
  );
}